import type { FeedbackSelection } from './inspect'
import { blobToBase64, type AudioCapture } from './audio'
import { readSiteVersion } from './site-version'
import { createWithdrawalToken, rememberWithdrawalToken } from './withdrawal-client'

export type IngestResult = {
  todoKey: string
  todoId: string
  openUrl: string
  siteVersion?: string | null
}

export async function submitFeedback(input: {
  apiBase: string
  token: string
  selection: FeedbackSelection
  text: string
  audio: AudioCapture | null
}): Promise<IngestResult> {
  const message: Record<string, unknown> = {}
  const withdrawalToken = createWithdrawalToken()
  if (input.text.trim()) message.text = input.text.trim()
  if (input.audio) {
    message.audioBase64 = await blobToBase64(input.audio.blob)
    message.audioMimeType = input.audio.mimeType
    message.durationMs = input.audio.durationMs
  }

  const body = {
    schemaVersion: 1,
    withdrawalToken,
    sessionToken: input.token,
    page: {
      url: location.href.split('#')[0],
      locale: document.documentElement.lang || undefined,
      title: document.title,
      siteVersion: readSiteVersion(),
      viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
    },
    target: {
      feedbackId: input.selection.feedbackId,
      selector: input.selection.selector,
      xpath: input.selection.xpath,
      tag: input.selection.tag,
      textPreview: input.selection.textPreview,
      outerSnippet: input.selection.outerSnippet,
    },
    message,
  }

  const res = await fetch(`${input.apiBase}/api/site-feedback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${input.token}`,
      'Idempotency-Key': crypto.randomUUID(),
    },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as {
    data?: IngestResult
    error?: string
  }
  if (!res.ok) throw new Error(json.error || `Submit failed (${res.status})`)
  if (!json.data?.todoKey) throw new Error('Invalid ingest response')
  if (json.data.todoId) rememberWithdrawalToken(json.data.todoId, withdrawalToken)
  return json.data
}
